import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";
import { useSound } from "../../utils/sound/SoundProvider";
import SocketContext from "../../context/SocketContext";
import useGameBase from "../../hooks/useGameBase";

const getInitialSelected = () => [];

function isAdjacent(a, b) {
  return (
    Math.abs(a.row - b.row) <= 1 &&
    Math.abs(a.col - b.col) <= 1 &&
    !(a.row === b.row && a.col === b.col)
  );
}

function LetterLinkGame({
  gameState,
  gamePlayers,
  user,
  roomId,
}) {
  const state = useMemo(() => (gameState || {}), [gameState]);
  // Timer state for animated bar
  const [timeLeft, setTimeLeft] = useState(state.timeLimit || 120);
  const timeLimit = state.timeLimit || 120;
  const sound = useSound();
  const lastWordCountRef = useRef(0);
  const lastErrorRef = useRef("");
  const gridContainerRef = useRef(null);
  const commitCellRef = useRef(null);
  const pointerSelectionRef = useRef({
    active: false,
    pointerId: null,
    startCell: null,
    dragged: false,
    submitted: false,
  });
  // (removed unused showWordsFor state)

  useEffect(() => {
    // Use server-provided startTime as the canonical anchor so client
    // does not reset the remaining time when unrelated parts of gameState
    // change (e.g. player words being added).
    const limit = state.timeLimit || 120;
    setTimeLeft(limit);
    if (!state || state.finished) return;
    const startTime = state.startTime || Date.now();
    let raf;
    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const remaining = Math.max(0, Math.ceil(limit - elapsed));
      setTimeLeft((prev) => (prev === remaining ? prev : remaining));
      const pct = Math.min(elapsed / limit, 1);
      if (pct < 1 && !state.finished && state.phase === 'play') {
        raf = requestAnimationFrame(tick);
      }
      // Do not set finished on client; server controls finished state
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [state]);
  const socket = useContext(SocketContext);
  const { submitAction } = useGameBase({
    gameState,
    gamePlayers,
    user,
    socket,
    roomId,
  });
  // Inline styles for grid and button
  // Set cell size based on grid dimensions
  let cellSize = 70; // default px
  if (gameState.width >= 6 || gameState.height >= 6) {
    cellSize = 48;
  } else if (gameState.width >= 5 || gameState.height >= 5) {
    cellSize = 58;
  }

  const buttonStyle = {
    margin: "0.25rem",
    width: `${cellSize}px`,
    height: `${cellSize}px`,
    fontWeight: "bold",
    fontSize: `${Math.round(cellSize * 0.45)}px`,
    borderRadius: `${Math.round(cellSize * 0.15)}px`,
    border: "1px solid",
    opacity: 1,
    cursor: "pointer",
  };

  const disabledButtonStyle = {
    ...buttonStyle,
    opacity: 0.5,
    cursor: "not-allowed",
  };
  const [selected, setSelected] = useState(getInitialSelected());
  const [word, setWord] = useState("");
  const [words, setWords] = useState([]);
  const selectedRef = useRef([]);
  const wordRef = useRef("");
  const playerId = user?.socketId;
  const playerWords = useMemo(() => (
    playerId && Array.isArray(state?.words?.[playerId])
      ? state.words[playerId]
      : []
  ), [playerId, state?.words]);
  const playerErrorMessage = playerId && state?.errorMessageByPlayer
    ? state.errorMessageByPlayer[playerId] || ""
    : "";

  // Use user.socketId as playerId
  useEffect(() => {
    setWords(playerWords);
  }, [playerWords]);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    wordRef.current = word;
  }, [word]);

  // Play sound when a word is accepted or rejected
  useEffect(() => {
    if (!playerId) return;
    if (!sound || typeof sound.play !== 'function') return;

    if (playerWords.length > lastWordCountRef.current) {
      sound.play('ui:positive');
    }

    if (playerErrorMessage && playerErrorMessage !== lastErrorRef.current) {
      sound.play('ui:negative');
    }

    lastWordCountRef.current = playerWords.length;
    lastErrorRef.current = playerErrorMessage;
  }, [playerId, playerWords.length, playerErrorMessage, sound]);

  const resetSelection = useCallback(() => {
    commitCellRef.current = null;
    selectedRef.current = [];
    wordRef.current = "";
    setSelected([]);
    setWord("");
  }, []);

  const submitCurrentWord = useCallback((candidateWord) => {
    if (!candidateWord || candidateWord.length < 3) return false;
    submitAction({ type: "submitWord", word: candidateWord });
    return true;
  }, [submitAction]);

  const extendSelection = useCallback((row, col) => {
    const nextCell = { row, col };
    const prevSelected = selectedRef.current;
    const idx = prevSelected.findIndex((s) => s.row === row && s.col === col);
    if (idx !== -1) {
      const truncatedSelection = prevSelected.slice(0, idx + 1);
      const truncatedWord = truncatedSelection
        .map((cell) => state.grid[cell.row][cell.col])
        .join("");
      selectedRef.current = truncatedSelection;
      wordRef.current = truncatedWord;
      setSelected(truncatedSelection);
      setWord(truncatedWord);
      return true;
    }

    if (prevSelected.length > 0 && !isAdjacent(prevSelected[prevSelected.length - 1], nextCell)) {
      return false;
    }

    const nextSelected = [...prevSelected, nextCell];
    const nextWord = `${wordRef.current}${state.grid[row][col]}`;
    selectedRef.current = nextSelected;
    wordRef.current = nextWord;
    setSelected(nextSelected);
    setWord(nextWord);
    return true;
  }, [state.grid]);

  // Double-click detection for pointer up: count-based, not time-based
  const lastPointerUpRef = useRef({ row: null, col: null, count: 0 });

  const handleCellSelection = useCallback((row, col, isDoubleClick = false) => {
    const currentSelection = selectedRef.current;
    const idx = currentSelection.findIndex((s) => s.row === row && s.col === col);

    if (idx === -1) {
      commitCellRef.current = null;
      extendSelection(row, col);
      return;
    }

    if (idx === currentSelection.length - 1) {
      if (isDoubleClick) {
        if (currentSelection.length >= 3) {
          submitCurrentWord(wordRef.current);
        }
        // Always clear selection on double click, even if not enough letters
        resetSelection();
        return;
      }
      commitCellRef.current = { row, col };
      return;
    }

    const truncatedSelection = currentSelection.slice(0, idx + 1);
    const truncatedWord = truncatedSelection
      .map((cell) => state.grid[cell.row][cell.col])
      .join("");
    setSelected(truncatedSelection);
    setWord(truncatedWord);
    selectedRef.current = truncatedSelection;
    wordRef.current = truncatedWord;
    commitCellRef.current = null;
  }, [extendSelection, resetSelection, state.grid, submitCurrentWord]);

  // For drag: require pointer near center. For click: allow anywhere.
  const getCellFromPoint = useCallback((clientX, clientY, opts = {}) => {
    if (!gridContainerRef.current) return null;
    const element = document.elementFromPoint(clientX, clientY);
    if (!element) return null;
    const cellElement = element.closest("[data-letterlink-cell]");
    if (!cellElement || !gridContainerRef.current.contains(cellElement)) return null;

    const row = Number(cellElement.getAttribute("data-row"));
    const col = Number(cellElement.getAttribute("data-col"));
    if (!Number.isInteger(row) || !Number.isInteger(col)) return null;

    if (opts.requireCenter) {
      // Only for drag
      const rect = cellElement.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dx = clientX - centerX;
      const dy = clientY - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const maxDist = Math.min(rect.width, rect.height) * 0.4; // 40% of cell size
      if (dist > maxDist) return null;
    }

    return { row, col };
  }, []);

  const endPointerSelection = useCallback((shouldSubmit) => {
    const ref = pointerSelectionRef.current;
    if (ref.pointerId !== null && gridContainerRef.current?.releasePointerCapture) {
      try {
        gridContainerRef.current.releasePointerCapture(ref.pointerId);
      } catch (error) {}
    }

    pointerSelectionRef.current = {
      active: false,
      pointerId: null,
      startCell: null,
      dragged: false,
      submitted: false,
    };

    if (shouldSubmit) {
      submitCurrentWord(wordRef.current);
      resetSelection();
    }
  }, [resetSelection, submitCurrentWord]);

  const handlePointerDown = useCallback((event, row, col) => {
    if (state.finished) return;
    commitCellRef.current = null;
    pointerSelectionRef.current = {
      active: true,
      pointerId: event.pointerId,
      startCell: { row, col },
      dragged: false,
      submitted: false,
    };
    if (gridContainerRef.current?.setPointerCapture) {
      try {
        gridContainerRef.current.setPointerCapture(event.pointerId);
      } catch (error) {}
    }
  }, [state.finished]);

  const handlePointerMove = useCallback((event) => {
    const ref = pointerSelectionRef.current;
    if (!ref.active || ref.pointerId !== event.pointerId) return;
    // For drag, require center
    const cell = getCellFromPoint(event.clientX, event.clientY, { requireCenter: true });
    if (!cell) return;

    if (!ref.dragged && ref.startCell) {
      const initialCell = ref.startCell;
      const initialWord = state.grid[initialCell.row][initialCell.col] || "";
      selectedRef.current = [initialCell];
      wordRef.current = initialWord;
      setSelected([initialCell]);
      setWord(initialWord);
    }

    const updated = extendSelection(cell.row, cell.col);
    if (updated) {
      ref.dragged = true;
    }
  }, [extendSelection, getCellFromPoint, state.grid]);

  const handlePointerUp = useCallback((event) => {
    const ref = pointerSelectionRef.current;
    if (!ref.active || ref.pointerId !== event.pointerId) return;
    // For click, do NOT require center
    const cell = getCellFromPoint(event.clientX, event.clientY, { requireCenter: false });
    const endedOnStartCell = Boolean(
      cell &&
      ref.startCell &&
      cell.row === ref.startCell.row &&
      cell.col === ref.startCell.col
    );

    if (!ref.dragged && endedOnStartCell) {
      // Double-click detection (count-based)
      const last = lastPointerUpRef.current;
      let isDoubleClick = false;
      if (last && last.row === cell.row && last.col === cell.col) {
        lastPointerUpRef.current = { row: cell.row, col: cell.col, count: last.count + 1 };
        if (last.count + 1 >= 2) {
          isDoubleClick = true;
          // Reset count after double click
          lastPointerUpRef.current = { row: null, col: null, count: 0 };
        }
      } else {
        lastPointerUpRef.current = { row: cell.row, col: cell.col, count: 1 };
      }
      handleCellSelection(cell.row, cell.col, isDoubleClick);
      endPointerSelection(false);
      return;
    }

    endPointerSelection(ref.dragged);
  }, [endPointerSelection, getCellFromPoint, handleCellSelection]);
  // Clear selection when clicking outside the grid
  useEffect(() => {
    function handleDocumentPointerDown(e) {
      if (!gridContainerRef.current) return;
      if (!gridContainerRef.current.contains(e.target)) {
        resetSelection();
      }
    }
    document.addEventListener("pointerdown", handleDocumentPointerDown);
    return () => document.removeEventListener("pointerdown", handleDocumentPointerDown);
  }, [resetSelection]);

  const handlePointerCancel = useCallback((event) => {
    const ref = pointerSelectionRef.current;
    if (!ref.active || ref.pointerId !== event.pointerId) return;
    endPointerSelection(false);
  }, [endPointerSelection]);

  if (!state || !state.grid) return <div>Loading...</div>;
  // Only set gridStyle if grid exists
  const gridStyle = {
    display: "grid",
    gap: "0.25rem",
    gridTemplateRows: `repeat(${state.height}, ${cellSize}px)`,
    gridTemplateColumns: `repeat(${state.width}, ${cellSize}px)`,
    touchAction: "none",
  };


  // ...existing code...

  return (
    <div className="dark:bg-gray-900 dark:text-white flex flex-col items-center justify-center">
      <style>{`
        .letterlink-grid { display: grid; gap: 0.25rem; }
        .letterlink-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .letterlink-selected {
          border: 2px solid #2563eb !important;
        }
        .dark .letterlink-selected {
          border: 2px solid #c8d602ff !important;
        }
      `}</style>

      {!state.finished && (
        // ...existing code for timer and grid...
        <>
          <div className="w-full max-w-md mx-auto mt-2 mb-2">
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
              <div
                className="h-full bg-yellow-400 transition-none"
                style={{
                  width: `${(1 - timeLeft / timeLimit) * 100}%`,
                  transition: "width 0.1s linear",
                }}
              ></div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">
              {timeLeft}s left
            </div>
          </div>
          <div
            ref={gridContainerRef}
            className="letterlink-grid"
            style={gridStyle}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          >
            {state.grid.map((row, rIdx) =>
              row.map((cell, cIdx) => {
                const isSelected = selected.some(
                  (s) => s.row === rIdx && s.col === cIdx
                );
                return (
                  <button
                    key={`${rIdx}-${cIdx}`}
                    data-letterlink-cell="true"
                    data-row={rIdx}
                    data-col={cIdx}
                    className={`letterlink-btn dark:bg-gray-800 dark:border-gray-600 border text-lg font-bold rounded${
                      isSelected ? " letterlink-selected" : ""
                    } ${isSelected ? "dark:bg-yellow-900 bg-blue-300" : ""}`}
                    style={state.finished ? disabledButtonStyle : buttonStyle}
                    onPointerDown={(event) => handlePointerDown(event, rIdx, cIdx)}
                    disabled={state.finished}
                  >
                    {cell}
                  </button>
                );
              })
            )}
          </div>
          <div className="mt-4">
            <div>
              Current Word: <span className="font-mono">{word}</span>
            </div>
            {/* Show per-player error message if available */}
            {(() => {
              const playerId = user && user.socketId;
              const msg = state.errorMessageByPlayer && playerId ? state.errorMessageByPlayer[playerId] : "";
              return msg ? (
                <div className="text-red-600 font-bold mt-2">{msg}</div>
              ) : null;
            })()}
            <div className="mt-2">Your Words:</div>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {/* ...existing code for word columns... */}
              <div>
                <div className="font-bold text-xs mb-1">3 Letters</div>
                {words.filter(w => w.length === 3).map((w, i) => (
                  <div key={"3-"+i} className="bg-gray-100 dark:bg-gray-800 rounded px-2 py-1 text-center text-sm font-mono border border-gray-300 dark:border-gray-700">{w}</div>
                ))}
              </div>
              <div>
                <div className="font-bold text-xs mb-1">4 Letters</div>
                {words.filter(w => w.length === 4).map((w, i) => (
                  <div key={"4-"+i} className="bg-gray-100 dark:bg-gray-800 rounded px-2 py-1 text-center text-sm font-mono border border-gray-300 dark:border-gray-700">{w}</div>
                ))}
              </div>
              <div>
                <div className="font-bold text-xs mb-1">5 Letters</div>
                {words.filter(w => w.length === 5).map((w, i) => (
                  <div key={"5-"+i} className="bg-gray-100 dark:bg-gray-800 rounded px-2 py-1 text-center text-sm font-mono border border-gray-300 dark:border-gray-700">{w}</div>
                ))}
              </div>
              <div>
                <div className="font-bold text-xs mb-1">6+ Letters</div>
                {words.filter(w => w.length >= 6).map((w, i) => (
                  <div key={"6plus-"+i} className="bg-gray-100 dark:bg-gray-800 rounded px-2 py-1 text-center text-sm font-mono border border-gray-300 dark:border-gray-700">{w}</div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Show player scores and all words at game end, grouped by word length and pill format */}
      {state.finished && (
        <div className="mt-6 w-full max-w-lg mx-auto">
          <h2 className="text-xl font-bold mb-4">Scores</h2>
          <ul className="divide-y divide-gray-300 dark:divide-gray-700">
            {gamePlayers.map((p) => {
              const pid = p.socketId || p.userId || p.id;
              const score = state.scores && state.scores[pid] ? state.scores[pid] : 0;
              const playerWords = state.words && state.words[pid] ? state.words[pid] : [];
              return (
                <li key={pid} className="py-2">
                  <div className="flex items-center gap-2 w-full text-left px-2 py-1 rounded">
                    <span style={{ backgroundColor: p.color || '#e57373', borderRadius: '4px', padding: '2px 6px', display: 'inline-block' }}>{p.icon || '😀'}</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{p.username}</span>
                    <span className="ml-auto font-mono text-lg font-bold">{score}</span>
                  </div>
                  <div className="mt-2 ml-8 grid grid-cols-4 gap-2">
                    {/* 3-letter words */}
                    <div>
                      <div className="font-bold text-xs mb-1">3 Letters</div>
                      {playerWords.filter(w => w.length === 3).map((w, i) => (
                        <div key={"3-"+i} className="bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5 text-center text-xs font-mono border border-gray-300 dark:border-gray-700">{w}</div>
                      ))}
                    </div>
                    {/* 4-letter words */}
                    <div>
                      <div className="font-bold text-xs mb-1">4 Letters</div>
                      {playerWords.filter(w => w.length === 4).map((w, i) => (
                        <div key={"4-"+i} className="bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5 text-center text-xs font-mono border border-gray-300 dark:border-gray-700">{w}</div>
                      ))}
                    </div>
                    {/* 5-letter words */}
                    <div>
                      <div className="font-bold text-xs mb-1">5 Letters</div>
                      {playerWords.filter(w => w.length === 5).map((w, i) => (
                        <div key={"5-"+i} className="bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5 text-center text-xs font-mono border border-gray-300 dark:border-gray-700">{w}</div>
                      ))}
                    </div>
                    {/* 6+ letter words */}
                    <div>
                      <div className="font-bold text-xs mb-1">6+ Letters</div>
                      {playerWords.filter(w => w.length >= 6).map((w, i) => (
                        <div key={"6plus-"+i} className="bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5 text-center text-xs font-mono border border-gray-300 dark:border-gray-700">{w}</div>
                      ))}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

LetterLinkGame.Guide = function LetterLinkGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Letter Link</h3>
      <ul className="list-disc ml-6">
        <li>Form words by connecting adjacent letters in the grid.</li>
        <li>Words must be at least 3 letters long.</li>
        <li>Submit your words before the time runs out!</li>
      </ul>
    </div>
  );
};

export default LetterLinkGame;
